/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { HomeShortcutService } from "@tivocorp/data-domain-lib";
import type {
  THomeShortcutFetchShortcutsParams,
  TServicesParams,
  QuickMenuShortcutV4
} from "@tivocorp/data-domain-lib";

import type { IHomeScreenInteractor } from "../interfaces/IHomeScreenInteractor";
import { DiagnosticLogger } from "../logging/DiagnosticLogger";
import { Log } from "../logging/Log";
import { OperationalLogger } from "../logging/OperationalLogger";
import { PostSigninApi, PostSigninLogger } from "../logging";
import { DeviceModel } from "../device";
import { TokenManager } from "../auth/TokenManager";
import { BaseInteractor } from "./base";
import {
  StorageAdapter,
  STORAGE_KEY_LAST_APP_VERSION,
  STORAGE_KEY_QUICK_MENU_SHORTCUT,
  STORAGE_KEY_QUICK_MENU_SHORTCUT_LANGUAGE
} from "../storage";
import { RetryMechanism } from "../retrymechanism";
import { DiagnosticSeverity, RETRY_POLICY, type TRetryOptions } from "../types";
import { ApplicationFeatureArea } from "../enums";
import { DEFAULT_LANGUAGE, TIVO_SERVICES } from "../../config";
import {
  HOMESCREEN_CAROUSEL_SCREEN_ID,
  QUICK_MENU_API_ENDPOINT,
  QUICK_MENU_DIAGNOSTIC_EVENT
} from "../constants/quickMenuTelemetryConstants";

const QuickMenuOperationalLogger = OperationalLogger.create({
  screenId: HOMESCREEN_CAROUSEL_SCREEN_ID,
  apiEndpoint: QUICK_MENU_API_ENDPOINT
});
const QUICK_MENU_FALLBACK_MESSAGE: string =
  "Quick Menu request failed; using fallback";

/**
 * Facade class that wraps HomeShortcutService behind a single entry
 * point for fetching quick-menu shortcuts on the HomeScreen.
 *
 * Service instances are **not** stored as class members. A fresh
 * instance is created on every method call so the interactor holds
 * no mutable state. TServicesParams is built internally; callers
 * do not supply it.
 *
 * Caching, defaults and fallback logic live in `HomeShortcutService`
 * (domain-lib data layer). This interactor only owns session/version
 * bypass detection and delegates everything else.
 */
export class HomeScreenInteractor
  extends BaseInteractor
  implements IHomeScreenInteractor
{
  private static readonly TAG: string = "HomeScreenInteractor";

  /**
   * In-memory runtime flag (not application data) that tracks whether
   * `isNewSession()` has fired in this JavaScript runtime invocation.
   * Resets to `false` whenever the runtime restarts (browser close / device
   * reboot), giving the same semantic as sessionStorage without using any
   * Web Storage API forbidden by StorageGuidelines §2.
   */
  private static _sessionInitialized: boolean = false;

  /**
   * Builds the TServicesParams used to instantiate HomeShortcutService.
   * Looks up the quick-menu base URL from the TSRI **service** endpoints
   * (not global endpoints). Falls back to empty basePath if not found.
   */
  private async buildHomeServicesParams(): Promise<TServicesParams> {
    try {
      return await this.buildServicesParams(
        TIVO_SERVICES.QuickMenu,
        ApplicationFeatureArea.HOME
      );
    } catch (error: unknown) {
      Log.w(
        HomeScreenInteractor.TAG,
        "quick-menu not found in serviceEndpoints, using fallback basePath",
        error
      );
      const accessToken: string = await TokenManager.getValidAccessToken();
      const bodyId: string = await DeviceModel.getTiVoSettings().getBodyId();

      return {
        basePath: "",
        acceptLanguage:
          DeviceModel.getTVProperties().getDateLocale() || "en-US",
        applicationFeatureArea: ApplicationFeatureArea.HOME,
        applicationName: DeviceModel.getAppName(),
        applicationVersion: DeviceModel.getAppVersionName(),
        deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
        productName: DeviceModel.getAppName(),
        accessToken,
        bodyId
      };
    }
  }

  // ─── Session / version helpers ───────────────────────────────────────────────

  /**
   * Returns true the first time it is called in the current JavaScript runtime
   * (i.e. a fresh browser session / device reboot proxy). The static flag
   * resets when the runtime restarts, so subsequent calls within the same
   * session return false.
   */
  private static isNewSession(): boolean {
    if (!HomeScreenInteractor._sessionInitialized) {
      HomeScreenInteractor._sessionInitialized = true;
      return true;
    }
    return false;
  }

  /**
   * Invalidates QuickMenu shortcut cache when the Accept-Language changes.
   */
  private static async invalidateQuickMenuCacheIfLocaleChanged(): Promise<void> {
    const isLanguageSupported: boolean =
      DeviceModel.getTVProperties().hasSupportedLanguage();
    const currentLanguage: string = isLanguageSupported
      ? DeviceModel.getTVProperties().getLang()
      : DEFAULT_LANGUAGE;
    const storage: StorageAdapter = StorageAdapter.getInstance();

    try {
      const storedLanguage: string | null = await storage.get<string>(
        STORAGE_KEY_QUICK_MENU_SHORTCUT_LANGUAGE
      );

      if (storedLanguage === currentLanguage) return;

      if (storedLanguage !== null) {
        Log.i(
          HomeScreenInteractor.TAG,
          `Language changed: "${storedLanguage}" → "${currentLanguage}" — clearing QuickMenu cache`
        );
      }

      await storage.delete(STORAGE_KEY_QUICK_MENU_SHORTCUT);
      await storage.set(
        STORAGE_KEY_QUICK_MENU_SHORTCUT_LANGUAGE,
        currentLanguage,
        Number.POSITIVE_INFINITY
      );
    } catch (error: unknown) {
      Log.w(
        HomeScreenInteractor.TAG,
        "Failed to validate QuickMenu locale cache — proceeding",
        error
      );
    }
  }

  /**
   * Returns true when the app version differs from the value stored at the
   * previous launch. Always writes the current version back to persistent
   * storage so the next launch can compare against it.
   */
  private static async isAppUpdated(): Promise<boolean> {
    const currentVersion: string = DeviceModel.getAppVersionName();
    try {
      const stored: string | null =
        await StorageAdapter.getInstance().get<string>(
          STORAGE_KEY_LAST_APP_VERSION
        );
      const changed: boolean = stored !== currentVersion;
      if (changed) {
        const storedForLog: string = stored === null ? "null" : stored;
        Log.i(
          HomeScreenInteractor.TAG,
          `isAppUpdated(): version changed ${storedForLog} → ${currentVersion}`
        );
      }
      await StorageAdapter.getInstance().set(
        STORAGE_KEY_LAST_APP_VERSION,
        currentVersion,
        Number.POSITIVE_INFINITY
      );
      return changed;
    } catch (error: unknown) {
      Log.w(
        HomeScreenInteractor.TAG,
        "isAppUpdated(): storage error — treating as updated",
        error
      );
      return true;
    }
  }

  /**
   * Returns true when the cache must be bypassed for this launch:
   * either the browser session is new (reboot proxy) or the app was updated.
   */
  private static async shouldBypassCache(): Promise<boolean> {
    const isNewSession: boolean = HomeScreenInteractor.isNewSession();
    const isUpdated: boolean = await HomeScreenInteractor.isAppUpdated();
    return isNewSession || isUpdated;
  }

  // ─── Public launch-time entry point ──────────────────────────────────────────

  /**
   * Loads quick-menu shortcuts at app launch, applying the full cache policy:
   *
   * - No bypass (normal launch): delegates to {@link fetchQuickMenuShortcutsWithFallback},
   *   which handles cache-hit → skip API, cache-miss → fetch → write cache,
   *   and API failure → cached data or hardcoded defaults.
   * - Bypass (reboot or app update): calls {@link fetchQuickMenuShortcuts} directly
   *   to force a fresh API call; on failure falls back via
   *   {@link fallbackQuickMenuShortcutsWithRetry} — returns cached or default data
   *   (with a background CRITICAL retry).
   *
   *   Note: HTTP cache validation (ETag / 304 / Cache-Control max-age) is
   *   handled natively by the browser for Chromium-based targets.
   */
  public async loadMenuShortcutsAtAppLaunch(
    params: THomeShortcutFetchShortcutsParams,
    signal?: AbortSignal,
    onRetrySuccess?: (shortcuts: QuickMenuShortcutV4[]) => void
  ): Promise<QuickMenuShortcutV4[]> {
    await HomeScreenInteractor.invalidateQuickMenuCacheIfLocaleChanged();

    const bypass: boolean = await HomeScreenInteractor.shouldBypassCache();

    if (!bypass) {
      Log.d(
        HomeScreenInteractor.TAG,
        "loadMenuShortcutsAtAppLaunch(): no bypass — using cache-first strategy"
      );
      return this.fetchQuickMenuShortcutsWithFallback(params, signal);
    }

    Log.i(
      HomeScreenInteractor.TAG,
      "loadMenuShortcutsAtAppLaunch(): bypassing cache (reboot or app update) — calling API directly"
    );
    try {
      const shortcuts = await this.fetchQuickMenuShortcuts(params, signal);
      if (shortcuts.length === 0) {
        Log.w(
          HomeScreenInteractor.TAG,
          "loadMenuShortcutsAtAppLaunch(): API returned empty list — falling back to cache/defaults"
        );
        QuickMenuOperationalLogger.logError({
          errorMessage: "Quick Menu API returned no shortcuts; using fallback"
        });
        DiagnosticLogger.log(
          QUICK_MENU_DIAGNOSTIC_EVENT,
          "Quick Menu API returned no shortcuts; using fallback",
          DiagnosticSeverity.Warning,
          { event: "fallback_used", reason: "empty_response" }
        );
        return this.getCachedOrDefaultQuickMenuShortcuts();
      }
      return shortcuts;
    } catch (error: unknown) {
      Log.w(
        HomeScreenInteractor.TAG,
        "loadMenuShortcutsAtAppLaunch(): API failed — falling back to cache/defaults",
        error
      );
      QuickMenuOperationalLogger.logApiFailure(error);
      QuickMenuOperationalLogger.logError({
        errorMessage: QUICK_MENU_FALLBACK_MESSAGE
      });
      DiagnosticLogger.log(
        QUICK_MENU_DIAGNOSTIC_EVENT,
        QUICK_MENU_FALLBACK_MESSAGE,
        DiagnosticSeverity.Warning,
        {
          event: "fallback_used",
          error_type: OperationalLogger.describeErrorType(error)
        }
      );
      return this.fallbackQuickMenuShortcutsWithRetry(
        params,
        signal,
        onRetrySuccess
      );
    }
  }

  /**
   * Returns cached or default shortcuts. If cached/default data is available schedules
   * a background retry with CRITICAL policy to refresh the API data.
   */
  private async fallbackQuickMenuShortcutsWithRetry(
    params: THomeShortcutFetchShortcutsParams,
    signal?: AbortSignal,
    onRetrySuccess?: (shortcuts: QuickMenuShortcutV4[]) => void
  ): Promise<QuickMenuShortcutV4[]> {
    const cachedOrDefaultQuickMenuShortcuts: QuickMenuShortcutV4[] =
      await this.getCachedOrDefaultQuickMenuShortcuts();

    this.retryQuickMenuShortcutsInBackground(params, signal, onRetrySuccess);
    return cachedOrDefaultQuickMenuShortcuts;
  }

  /**
   * Fire-and-forget background retry using CRITICAL policy.
   * On success the service-layer cache is updated for subsequent reads.
   */
  private retryQuickMenuShortcutsInBackground(
    params: THomeShortcutFetchShortcutsParams,
    signal?: AbortSignal,
    onRetrySuccess?: (shortcuts: QuickMenuShortcutV4[]) => void
  ): void {
    const retryOptions: TRetryOptions = {
      retryPolicy: RETRY_POLICY.CRITICAL,
      httpMethod: "GET"
    };

    const retryFn = async (): Promise<QuickMenuShortcutV4[]> => {
      return this.fetchQuickMenuShortcuts(params, signal);
    };

    RetryMechanism.callServiceWithRetry<QuickMenuShortcutV4[]>(
      retryFn as (...args: Array<unknown>) => Promise<QuickMenuShortcutV4[]>,
      [],
      retryOptions,
      signal
    )
      .then((shortcuts: QuickMenuShortcutV4[]) => {
        Log.i(
          HomeScreenInteractor.TAG,
          `retryQuickMenuShortcutsInBackground(): retry succeeded with ${shortcuts.length} items`
        );
        DiagnosticLogger.log(
          QUICK_MENU_DIAGNOSTIC_EVENT,
          "Quick Menu background retry recovered",
          DiagnosticSeverity.Info,
          {
            event: "background_retry_succeeded",
            shortcut_count: String(shortcuts.length)
          }
        );
        if (onRetrySuccess !== undefined) {
          onRetrySuccess(shortcuts);
        }
      })
      .catch((retryError: unknown) => {
        Log.w(
          HomeScreenInteractor.TAG,
          "retryQuickMenuShortcutsInBackground(): retry failed",
          retryError
        );
        QuickMenuOperationalLogger.logApiFailure(retryError);
        DiagnosticLogger.log(
          QUICK_MENU_DIAGNOSTIC_EVENT,
          "Quick Menu background retry ended without recovering",
          DiagnosticSeverity.Warning,
          {
            event: "background_retry_failed",
            aborted: String(signal && signal.aborted === true),
            error_type: OperationalLogger.describeErrorType(retryError)
          }
        );
      });
  }

  // ─── Thin delegates to HomeShortcutService ───────────────────────────────────

  public async fetchQuickMenuShortcuts(
    params: THomeShortcutFetchShortcutsParams,
    signal?: AbortSignal
  ): Promise<QuickMenuShortcutV4[]> {
    const service: HomeShortcutService = new HomeShortcutService(
      await this.buildHomeServicesParams()
    );
    const startTimeMs: number = Date.now();
    try {
      const shortcuts: QuickMenuShortcutV4[] =
        await service.fetchQuickMenuShortcuts(params, signal);
      PostSigninLogger.logApiSuccess(
        PostSigninApi.QuickMenuShortcuts,
        Date.now() - startTimeMs
      );
      return shortcuts;
    } catch (error: unknown) {
      PostSigninLogger.logApiFailure(
        PostSigninApi.QuickMenuShortcuts,
        error,
        DiagnosticSeverity.Error,
        Date.now() - startTimeMs
      );
      throw error;
    }
  }

  public async fetchQuickMenuShortcutsWithFallback(
    params: THomeShortcutFetchShortcutsParams,
    signal?: AbortSignal
  ): Promise<QuickMenuShortcutV4[]> {
    const service: HomeShortcutService = new HomeShortcutService(
      await this.buildHomeServicesParams()
    );
    try {
      return await service.getQuickMenuShortcuts(params, signal);
    } catch (error: unknown) {
      QuickMenuOperationalLogger.logApiFailure(error);
      QuickMenuOperationalLogger.logError({
        errorMessage: QUICK_MENU_FALLBACK_MESSAGE
      });
      DiagnosticLogger.log(
        QUICK_MENU_DIAGNOSTIC_EVENT,
        QUICK_MENU_FALLBACK_MESSAGE,
        DiagnosticSeverity.Warning,
        {
          event: "fallback_used",
          error_type: OperationalLogger.describeErrorType(error)
        }
      );
      return service.getCachedOrDefaultQuickMenuShortcuts();
    }
  }

  /**
   * Returns cached shortcuts from IndexedDB, or hardcoded defaults if the
   * cache is empty / unreadable.  This method never calls the API but uses
   * {@link buildHomeServicesParams} to construct the service with real params.
   */
  public async getCachedOrDefaultQuickMenuShortcuts(): Promise<
    QuickMenuShortcutV4[]
  > {
    const service: HomeShortcutService = new HomeShortcutService(
      await this.buildHomeServicesParams()
    );
    return service.getCachedOrDefaultQuickMenuShortcuts();
  }
}
